import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, X } from 'lucide-react';
import { getRenters, type Renter } from '../api';
import SortableTh, { type SortDir } from '../components/SortableTh';
import Pagination from '../components/Pagination';
import { useSettings } from '../context/SettingsContext';
import { formatDate } from '../utils/dates';
import { PageHeader, Avatar } from '../components/ui';
import RenterDetailModal from '../components/RenterDetailModal';

type SortKey = 'name' | 'email' | 'phone' | 'city' | 'rental_count' | 'created_at';

function getInitials(name: string | undefined): string {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2);
  return (parts[0][0] ?? '') + (parts[parts.length - 1][0] ?? '');
}

export default function RentersPage() {
  const { t } = useTranslation();
  const { settings } = useSettings();
  const [renters, setRenters] = useState<Renter[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch]   = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('name');
  const [sortDir, setSortDir] = useState<SortDir>('asc');
  const [page, setPage]       = useState(1);
  const [viewRenterId, setViewRenterId] = useState<number | null>(null);

  useEffect(() => {
    getRenters().then(setRenters).finally(() => setLoading(false));
  }, []);

  useEffect(() => { setPage(1); }, [search, sortKey, sortDir]);

  const handleSort = (k: SortKey) => {
    if (sortKey === k) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(k); setSortDir('asc'); }
  };

  const filtered = renters.filter(r => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (r.name?.toLowerCase().includes(q))
        || (r.email?.toLowerCase().includes(q) ?? false)
        || (r.phone?.includes(q) ?? false)
        || (r.city?.toLowerCase().includes(q) ?? false);
  });

  const sorted = [...filtered].sort((a, b) => {
    let av: string | number = '', bv: string | number = '';
    switch (sortKey) {
      case 'name':         av = a.name ?? '';                bv = b.name ?? ''; break;
      case 'email':        av = a.email ?? '';               bv = b.email ?? ''; break;
      case 'phone':        av = a.phone ?? '';               bv = b.phone ?? ''; break;
      case 'city':         av = a.city ?? '';                bv = b.city ?? ''; break;
      case 'rental_count': av = Number(a.rental_count);      bv = Number(b.rental_count); break;
      case 'created_at':   av = a.created_at;                bv = b.created_at; break;
    }
    const cmp = av < bv ? -1 : av > bv ? 1 : 0;
    return sortDir === 'asc' ? cmp : -cmp;
  });

  const pageSize = settings.itemsPerPage;
  const totalPages = Math.ceil(sorted.length / pageSize);
  const paged = sorted.slice((page - 1) * pageSize, page * pageSize);

  return (
    <main className="max-w-7xl mx-auto px-6 md:px-10 py-10">
      <PageHeader
        eyebrow={t('nav.admin_group')}
        title={t('renters.title')}
        subtitle={`${filtered.length} ${t('renters.count')}`}
      />

      {/* Søgning */}
      <div className="flex items-center gap-3 mb-4">
        <div className="relative flex-1 max-w-sm">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-300 pointer-events-none" />
          <input
            type="text"
            placeholder={t('renters.search_placeholder')}
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="filter-input w-full pl-9 pr-9 py-2"
          />
          {search && (
            <button
              onClick={() => setSearch('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-cream-100 text-ink-300 hover:text-ink-700"
              aria-label="Ryd søgning"
            >
              <X size={14} />
            </button>
          )}
        </div>
      </div>

      {/* Tabel-card */}
      <div className="list-card">
        {loading ? (
          <div className="p-12 text-center text-ink-500">…</div>
        ) : sorted.length === 0 ? (
          <div className="p-12 text-center text-ink-500">{t('renters.none')}</div>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <SortableTh keyName="name"         label={t('renters.col_name')}    sortKey={sortKey} sortDir={sortDir} onSort={handleSort} />
                <SortableTh keyName="email"        label={t('renters.col_email')}   sortKey={sortKey} sortDir={sortDir} onSort={handleSort} />
                <SortableTh keyName="phone"        label={t('renters.col_phone')}   sortKey={sortKey} sortDir={sortDir} onSort={handleSort} />
                <SortableTh keyName="city"         label={t('renters.col_city')}    sortKey={sortKey} sortDir={sortDir} onSort={handleSort} />
                <SortableTh keyName="rental_count" label={t('renters.col_rentals')} sortKey={sortKey} sortDir={sortDir} onSort={handleSort} alignRight />
                <SortableTh keyName="created_at"   label={t('renters.col_created')} sortKey={sortKey} sortDir={sortDir} onSort={handleSort} />
              </tr>
            </thead>
            <tbody>
              {paged.map(r => (
                <tr key={r.id} className="row-hover cursor-pointer" onClick={() => setViewRenterId(r.id)}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <Avatar initials={getInitials(r.name)} size="sm" />
                      <span className="font-medium text-ink-900">{r.name || '—'}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-ink-500 text-xs font-mono">{r.email || '—'}</td>
                  <td className="px-4 py-3 text-ink-500 text-xs font-mono">{r.phone || '—'}</td>
                  <td className="px-4 py-3 text-ink-700">{r.city || '—'}</td>
                  <td className="px-4 py-3 text-right font-mono text-ink-900 font-semibold">{r.rental_count}</td>
                  <td className="px-4 py-3 text-ink-500 text-xs">{formatDate(r.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-ink-100 text-sm text-ink-500">
            <span>{t('pagination.showing', { from: (page - 1) * pageSize + 1, to: Math.min(page * pageSize, sorted.length), total: sorted.length })}</span>
            <Pagination page={page} totalPages={totalPages} onChange={setPage} />
          </div>
        )}
      </div>

      {viewRenterId !== null && (
        <RenterDetailModal renterId={viewRenterId} onClose={() => setViewRenterId(null)} />
      )}
    </main>
  );
}
